import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { QrCode, Barcode, CreditCard, Copy, Check, CheckCircle2, Loader2, ExternalLink, ShieldCheck } from 'lucide-react'
import api from '../../api/client'

const API_BASE = (api?.defaults?.baseURL) || '/api'
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })

// Página de pagamento 100% TRANSPARENTE (PIX/boleto/cartão na própria tela).
// tipo: 'licenca' | 'renovacao' -> /indicacao/pagamento/:ref ; 'pedido' -> /loja/pagamento/:codigo
export default function Pagamento({ tipo = 'licenca' }) {
  const params = useParams()
  const id = params.ref || params.codigo
  const base = tipo === 'pedido' ? `/loja/pagamento/${id}` : `/indicacao/pagamento/${id}`

  const [info, setInfo] = useState(null)
  const [estado, setEstado] = useState('load')
  const [metodo, setMetodo] = useState(null)
  const [pix, setPix] = useState(null)
  const [boleto, setBoleto] = useState(null)
  const [parcelasOpts, setParcelasOpts] = useState([])
  const [loadingMet, setLoadingMet] = useState(false)
  const [pago, setPago] = useState(false)
  const [acesso, setAcesso] = useState(null)   // {primeiro_acesso_url, ja_tem_senha, email} pós-pagamento
  const [copiado, setCopiado] = useState('')
  const pagoRef = useRef(false)

  const _marcarPago = useCallback((d) => {
    setPago(true); pagoRef.current = true
    if (d && (d.primeiro_acesso_url || d.ja_tem_senha)) {
      setAcesso({ primeiro_acesso_url: d.primeiro_acesso_url, ja_tem_senha: !!d.ja_tem_senha, email: d.email })
    }
  }, [])

  useEffect(() => {
    api.get(base).then((r) => {
      setInfo(r.data); setEstado('ok')
      if (r.data.pago) {
        _marcarPago(r.data)
        // já estava pago ao abrir: busca o caminho de acesso (link de criar senha)
        api.get(`${base}/status`).then((s) => _marcarPago(s.data)).catch(() => {})
      }
    }).catch(() => setEstado('erro'))
  }, [base, _marcarPago])

  const checarStatus = useCallback(() => {
    if (pagoRef.current) return
    api.get(`${base}/status`).then((r) => { if (r.data.pago) _marcarPago(r.data) }).catch(() => {})
  }, [base, _marcarPago])
  useEffect(() => { const t = setInterval(checarStatus, 5000); return () => clearInterval(t) }, [checarStatus])

  // Abandono (só cadastro NOVO de licenciado): cria cadastro temporário se sair sem pagar
  useEffect(() => {
    if (tipo !== 'licenca') return
    const onLeave = () => { if (!pagoRef.current) { try { navigator.sendBeacon(`${API_BASE}${base}/abandono`, new Blob([JSON.stringify({})], { type: 'application/json' })) } catch { /* */ } } }
    window.addEventListener('beforeunload', onLeave)
    return () => window.removeEventListener('beforeunload', onLeave)
  }, [tipo, base])

  const escolher = (m) => {
    setMetodo(m)
    if (m === 'pix' && !pix) { setLoadingMet(true); api.post(`${base}/pix`).then((r) => setPix(r.data.pix)).catch(() => {}).finally(() => setLoadingMet(false)) }
    else if (m === 'boleto' && !boleto) { setLoadingMet(true); api.post(`${base}/boleto`).then((r) => setBoleto(r.data.boleto)).catch(() => {}).finally(() => setLoadingMet(false)) }
    else if (m === 'cartao' && parcelasOpts.length === 0) { api.get(`${base}/parcelas`).then((r) => setParcelasOpts(r.data.opcoes || [])).catch(() => {}) }
  }
  const copiar = (txt, tag) => { navigator.clipboard?.writeText(txt || ''); setCopiado(tag); setTimeout(() => setCopiado(''), 1600) }

  const Wrap = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-graphite via-neutral-900 to-accent-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
        <div className="font-heading text-xl font-extrabold text-graphite mb-1">The Conect <span className="text-accent-600">Energy</span></div>
        {children}
      </div>
    </div>
  )

  if (estado === 'load') return <Wrap><p className="text-gray-400 mt-4">Carregando pagamento...</p></Wrap>
  if (estado === 'erro') return <Wrap><p className="text-red-600 mt-4">Pagamento não encontrado ou expirado.</p></Wrap>

  if (pago) return (
    <Wrap>
      <div className="text-center py-6">
        <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-900">Pagamento confirmado!</h2>
        {tipo === 'renovacao' ? (
          <>
            <p className="text-sm text-gray-500 mt-2">Sua licença foi <b>renovada por mais 12 meses</b>. Obrigado!</p>
            <a href="/#/login" className="inline-block mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2.5 font-semibold">Acessar a plataforma</a>
          </>
        ) : tipo === 'pedido' ? (
          <>
            <p className="text-sm text-gray-500 mt-2">Recebemos o pagamento do seu pedido. Obrigado!</p>
            <a href="/#/login" className="inline-block mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2.5 font-semibold">Acessar a plataforma</a>
          </>
        ) : acesso?.primeiro_acesso_url ? (
          <>
            <p className="text-sm text-gray-500 mt-2">Sua licença foi <b>ativada</b>! Agora crie sua senha de acesso para entrar na plataforma.</p>
            <a href={acesso.primeiro_acesso_url}
              className="inline-block mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-6 py-3 font-bold text-base shadow-lg hover:brightness-105">
              Criar minha senha e acessar
            </a>
            <p className="text-[11px] text-gray-400 mt-3">Também enviamos este link por e-mail{acesso.email ? ` (${acesso.email})` : ''}. Verifique a caixa de spam.</p>
          </>
        ) : acesso?.ja_tem_senha ? (
          <>
            <p className="text-sm text-gray-500 mt-2">Sua licença foi <b>ativada</b>! Você já tem senha — entre com seu CPF ou e-mail.</p>
            <a href="/#/login" className="inline-block mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2.5 font-semibold">Acessar a plataforma</a>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500 mt-2">Sua licença foi <b>ativada</b>! Enviamos o link para <b>criar sua senha</b> por e-mail{' '}
              e WhatsApp. Verifique a caixa de entrada (e o spam).</p>
            <a href="/#/login" className="inline-block mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2.5 font-semibold">Já tenho senha — Entrar</a>
          </>
        )}
      </div>
    </Wrap>
  )

  return (
    <Wrap>
      <p className="text-xs uppercase tracking-widest text-accent-600 font-bold mb-1">
        {tipo === 'renovacao' ? 'Renovação da Licença' : tipo === 'pedido' ? 'Pagamento do Pedido' : 'Pagamento da Licença'}
      </p>
      <p className="text-2xl font-heading font-extrabold text-graphite mb-4">{money(info?.valor)}</p>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {[['pix', 'PIX', QrCode], ['boleto', 'Boleto', Barcode], ['cartao', 'Cartão', CreditCard]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => escolher(k)}
            className={`flex flex-col items-center gap-1 rounded-xl border py-3 text-sm font-semibold transition ${metodo === k ? 'border-accent-500 bg-accent-50 text-accent-700' : 'border-gray-200 text-gray-600 hover:border-accent-300'}`}>
            <Icon size={20} /> {label}
          </button>
        ))}
      </div>

      {loadingMet && <div className="flex items-center justify-center gap-2 text-gray-500 py-8"><Loader2 size={18} className="animate-spin" /> Gerando...</div>}

      {!loadingMet && metodo === 'pix' && pix && (
        <div className="text-center space-y-3">
          {pix.imagem && <img src={`data:image/png;base64,${pix.imagem}`} alt="QR PIX" className="w-52 h-52 mx-auto border border-gray-100 rounded-xl" />}
          <p className="text-xs text-gray-500">Escaneie o QR Code no app do seu banco ou copie o código:</p>
          <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 text-[11px] break-all text-gray-600 text-left">{pix.copia_cola}</div>
          <button onClick={() => copiar(pix.copia_cola, 'pix')} className="w-full rounded-lg bg-graphite text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
            {copiado === 'pix' ? <><Check size={16} /> Copiado!</> : <><Copy size={16} /> Copiar código PIX</>}
          </button>
        </div>
      )}

      {!loadingMet && metodo === 'boleto' && boleto && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Linha digitável do boleto:</p>
          <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 text-[12px] break-all text-gray-700">{boleto.linha_digitavel || '—'}</div>
          <div className="flex gap-2">
            <button onClick={() => copiar(boleto.linha_digitavel, 'bol')} className="flex-1 rounded-lg bg-graphite text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
              {copiado === 'bol' ? <><Check size={16} /> Copiado!</> : <><Copy size={16} /> Copiar</>}
            </button>
            {boleto.url && <a href={boleto.url} target="_blank" rel="noreferrer" className="flex-1 rounded-lg border border-gray-200 text-gray-700 py-2.5 text-sm font-semibold flex items-center justify-center gap-2"><ExternalLink size={15} /> Abrir boleto</a>}
          </div>
        </div>
      )}

      {metodo === 'cartao' && <CartaoForm base={base} opcoes={parcelasOpts} onPago={(d) => { _marcarPago(d); api.get(`${base}/status`).then((s) => _marcarPago(s.data)).catch(() => {}) }} />}

      {metodo && metodo !== 'cartao' && (
        <p className="text-[11px] text-gray-400 flex items-center justify-center gap-1.5 mt-4"><Loader2 size={12} className="animate-spin" /> Aguardando confirmação do pagamento...</p>
      )}
      {!metodo && <p className="text-xs text-gray-400 text-center mt-2">Escolha como deseja pagar.</p>}
    </Wrap>
  )
}

function CartaoForm({ base, opcoes, onPago }) {
  const [f, setF] = useState({ parcelas: 1, numero: '', nome_titular: '', validade: '', cvv: '', cpf: '', cep: '', numero_endereco: '', telefone: '' })
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'

  const pagar = () => {
    setErro('')
    if (!f.numero || !f.nome_titular || !f.validade || !f.cvv) return setErro('Preencha os dados do cartão.')
    setSaving(true)
    api.post(`${base}/cartao`, { ...f, parcelas: Number(f.parcelas) })
      .then((r) => { if (r.data.pago) onPago(r.data); else setErro(r.data.mensagem || 'Pagamento não aprovado.') })
      .catch((e) => setErro(e?.response?.data?.detail || 'Erro ao processar o cartão.'))
      .finally(() => setSaving(false))
  }
  return (
    <div className="space-y-2">
      {erro && <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{erro}</p>}
      <input placeholder="Número do cartão" value={f.numero} onChange={set('numero')} inputMode="numeric" className={inp} />
      <input placeholder="Nome impresso no cartão" value={f.nome_titular} onChange={set('nome_titular')} className={inp} />
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Validade (MM/AA)" value={f.validade} onChange={set('validade')} className={inp} />
        <input placeholder="CVV" value={f.cvv} onChange={set('cvv')} inputMode="numeric" className={inp} />
      </div>
      {opcoes.length > 0 && (
        <select value={f.parcelas} onChange={set('parcelas')} className={inp}>
          {opcoes.map((o) => (
            <option key={o.parcelas} value={o.parcelas}>
              {o.parcelas}x de {money(o.valor_parcela)} {o.tem_juros ? `(total ${money(o.total)})` : 'sem juros'}
            </option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="CPF do titular" value={f.cpf} onChange={set('cpf')} inputMode="numeric" className={inp} />
        <input placeholder="Telefone" value={f.telefone} onChange={set('telefone')} inputMode="numeric" className={inp} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="CEP" value={f.cep} onChange={set('cep')} inputMode="numeric" className={inp} />
        <input placeholder="Nº do endereço" value={f.numero_endereco} onChange={set('numero_endereco')} className={inp} />
      </div>
      <button onClick={pagar} disabled={saving} className="w-full rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <><Loader2 size={16} className="animate-spin" /> Processando...</> : <>Pagar com cartão</>}
      </button>
      <p className="text-[11px] text-gray-400 flex items-center justify-center gap-1"><ShieldCheck size={12} /> Pagamento processado com segurança (ASAAS). Seus dados não ficam armazenados na plataforma.</p>
    </div>
  )
}
